import React, { useCallback } from "react";
import AIActivityLog from "./AIActivityLog";
import AgentChatInput from "./AgentChatInput";
import ConnectionStatusBar from "./ConnectionStatusBar";
import IntegrationBuilderHeader from "./IntegrationBuilderHeader";
import IntegrationBuilderUrlInput from "./IntegrationBuilderUrlInput";
import IntegrationBuilderSummary from "./IntegrationBuilderSummary";
import IntegrationBuilderRightPanel from "./IntegrationBuilderRightPanel";
import { useIntegrationBuilder } from "./useIntegrationBuilder";

export default function IntegrationBuilder({ visibility, onClose, editingIntegration }) {
    const sim = useIntegrationBuilder({ editingIntegration });
    const showChat = sim.phase !== "url_input";

    const handleClose = () => { sim.reset(); onClose(); };
    const handleBaseUrlChange = useCallback((newUrl) => {
        sim.setApiInfo?.({ baseUrl: newUrl });
    }, [sim]);

    const chatFooter = showChat ? (
        <AgentChatInput onSend={sim.sendMessage} isSending={sim.isSending} />
    ) : null;

    const isTaskRunning = !!sim.taskId && sim.phase !== "url_input" && sim.phase !== "complete";
    const isChatLoading = sim.isSending || isTaskRunning;

    return (
        <div className="flex flex-col h-full">
            <IntegrationBuilderHeader phase={sim.phase} visibility={visibility} onClose={handleClose} integration={sim.integration} onBaseUrlChange={handleBaseUrlChange} />
            <ConnectionStatusBar isConnected={sim.isConnected} isPolling={sim.isPolling} connectionFailed={sim.connectionFailed} isRecovering={sim.isRecovering} phase={sim.phase} />
            <div className="flex-1 flex min-h-0 overflow-hidden">
                <div className="w-[340px] xl:w-[380px] shrink-0 border-r hidden lg:flex">
                    <AIActivityLog messages={sim.chatMessages} footer={chatFooter} isLoading={isChatLoading} />
                </div>
                <div className="flex-1 overflow-y-auto bg-gray-50 p-4 sm:p-6">
                    {sim.phase === "url_input" && (
                        <IntegrationBuilderUrlInput url={sim.url} setUrl={sim.setUrl} onSubmit={() => sim.submitUrl(visibility)} />
                    )}
                    {sim.phase !== "url_input" && sim.phase !== "complete" && (
                        <IntegrationBuilderRightPanel
                            phase={sim.phase} apiType={sim.apiType} endpoints={sim.endpoints}
                            revealedEndpoints={sim.revealedEndpoints} currentTestEndpoint={sim.currentTestEndpoint}
                            currentTestResult={sim.currentTestResult} currentFix={sim.currentFix}
                            credentials={sim.credentials} setCredentials={sim.setCredentials}
                            onStartTesting={sim.startTesting} onApproveEndpoint={sim.approveEndpoint}
                            onSkipEndpoint={sim.skipEndpoint} autoApprove={sim.autoApprove}
                            onToggleAutoApprove={sim.toggleAutoApprove} pendingParams={sim.pendingParams}
                            onSubmitParams={sim.submitParams} onRetestEndpoint={sim.retestEndpoint}
                            onFixWithAI={sim.fixWithAI} isFixing={sim.isFixing}
                            errorMap={sim.errorMap} revealedErrors={sim.revealedErrors} auth={sim.auth}
                            isTaskRunning={isTaskRunning} taskStartedAt={sim.taskStartedAt}
                            onSkipToPublish={sim.skipToComplete}
                        />
                    )}
                    {sim.phase === "complete" && (
                        <IntegrationBuilderSummary
                            apiType={sim.apiType} endpoints={sim.endpoints} approvedIds={sim.approvedIds}
                            errorMap={sim.errorMap} visibility={visibility} onPublish={onClose}
                            integration={sim.integration} auth={sim.auth} credentials={sim.credentials}
                            isEditing={!!editingIntegration} integrationId={editingIntegration?.id}
                            draftId={sim.draftId}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
